import noop from 'lodash/noop';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useClaimsFileUpload } from '@hooks/react-query/mutations/useClaimsFileUpload';
import { useAuth } from '@context/AuthContext';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import { ImportSettings } from './components/ImportsSettingsModal/ImportsSettingsModal.types';
import {
  COLUMN_NUMBER_MIN,
  EXPECTED_COLUMN_FIELDS,
  REQUIRED_COLUMN_GROUPS,
} from './constants';
import {
  ChipIconType,
  FileMatches,
  FileUploadView,
  NormalizeFile,
  RecalculateResponse,
} from './types';
import {
  detectHeaders,
  matchedFields,
  normalizeCSVData,
  parseCsvPreview,
} from './utils';

interface UploadClaimsFileContextProps {
  readonly formattedFile: NormalizeFile | null;
  readonly setFormattedFile: (file: NormalizeFile | null) => void;
  readonly delimiter: string;
  readonly setDelimiter: (delimiter: string) => void;
  readonly escapeChar: string;
  readonly setEscapeChar: (escapeChar: string) => void;
  readonly header: boolean;
  readonly setHeader: (header: boolean) => void;
  readonly fileMatches: FileMatches;
  readonly setFileMatches: (fileMatches: FileMatches) => void;
  readonly fileUploadView: FileUploadView;
  readonly setFileUploadView: (view: FileUploadView) => void;
  readonly helpDrawerOpen: boolean;
  readonly setHelpDrawerOpen: (open: boolean) => void;
  readonly importSettingsModalOpen: boolean;
  readonly setImportSettingsModalOpen: (open: boolean) => void;
  readonly selectedFilterChip: ChipIconType;
  readonly setSelectedFilterChip: (chip: ChipIconType) => void;
  readonly filteredMatches: FileMatches;
  readonly rawFile: File | null;
  readonly setRawFile: (file: File | null) => void;
  readonly recalculateFile: (
    settings: ImportSettings,
  ) => Promise<RecalculateResponse>;
  readonly handleFileUpload: () => Promise<void>;
  readonly isFileLoading: boolean;
  readonly uploadProgress: number;
  readonly uploadHasError: boolean;
  readonly setUploadHasError: (v: boolean) => void;
  readonly isAllRequiredFieldsMatched: boolean;
  readonly isAllRecommendedFieldsMatched: boolean;
  readonly requiredFields: number;
  readonly requiredMatchedFields: number;
}

export const UploadClaimsFileContext =
  createContext<UploadClaimsFileContextProps>({
    formattedFile: null,
    setFormattedFile: noop,
    delimiter: ',',
    setDelimiter: noop,
    escapeChar: '"',
    setEscapeChar: noop,
    header: false,
    setHeader: noop,
    fileMatches: [],
    setFileMatches: noop,
    fileUploadView: FileUploadView.UPLOAD,
    setFileUploadView: noop,
    helpDrawerOpen: false,
    setHelpDrawerOpen: noop,
    importSettingsModalOpen: false,
    setImportSettingsModalOpen: noop,
    rawFile: null,
    setRawFile: noop,
    recalculateFile: () => Promise.resolve({} as RecalculateResponse),
    selectedFilterChip: ChipIconType.ALL,
    setSelectedFilterChip: noop,
    filteredMatches: [],
    handleFileUpload: () => Promise.resolve(),
    isFileLoading: false,
    uploadProgress: 0,
    uploadHasError: false,
    setUploadHasError: noop,
    isAllRequiredFieldsMatched: false,
    isAllRecommendedFieldsMatched: false,
    requiredFields: 0,
    requiredMatchedFields: 0,
  });

export const SETTINGS_DEFAULT_VALUES = {
  DELIMITER: ',',
  ESCAPE_CHAR: '"',
  HEADER: false,
};

export function UploadClaimsFileProvider({ children }: PropsWithChildren) {
  const { selectedCase } = useUserOrgCase();
  const { user } = useAuth();
  const [rawFile, setRawFile] = useState<File | null>(null);
  const [formattedFile, setFormattedFile] = useState<NormalizeFile | null>(
    null,
  );
  const [delimiter, setDelimiter] = useState<string>(
    SETTINGS_DEFAULT_VALUES.DELIMITER,
  );
  const [escapeChar, setEscapeChar] = useState<string>(
    SETTINGS_DEFAULT_VALUES.ESCAPE_CHAR,
  );
  const [header, setHeader] = useState<boolean>(SETTINGS_DEFAULT_VALUES.HEADER);
  const [helpDrawerOpen, setHelpDrawerOpen] = useState(false);
  const [importSettingsModalOpen, setImportSettingsModalOpen] = useState(false);
  const [fileUploadView, setFileUploadView] = useState<FileUploadView>(
    FileUploadView.UPLOAD,
  );
  const [fileMatches, setFileMatches] = useState<FileMatches>([]);
  const [selectedFilterChip, setSelectedFilterChip] = useState<ChipIconType>(
    ChipIconType.ALL,
  );

  const { mutateAsync: submitBulkUploadFile, isPending } =
    useClaimsFileUpload();
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadHasError, setUploadHasError] = useState(false);

  const requiredFields = useMemo(
    () =>
      EXPECTED_COLUMN_FIELDS.filter((f) => f.required).length +
      REQUIRED_COLUMN_GROUPS.filter((g) => g.required).length,
    [],
  );

  const requiredMatchedFields = useMemo(
    () => fileMatches.filter((f) => f.required && f.matched).length,
    [fileMatches],
  );

  const isAllRequiredFieldsMatched = useMemo(
    () =>
      requiredMatchedFields === requiredFields &&
      fileMatches.filter((f) => f.required).length > 0,
    [requiredMatchedFields, requiredFields, fileMatches],
  );

  const isAllRecommendedFieldsMatched = useMemo(
    () =>
      EXPECTED_COLUMN_FIELDS.filter((f) => f.recommended).length ===
      fileMatches.filter((f) => f.recommended && f.matched).length,
    [fileMatches],
  );

  const filteredMatches = useMemo(() => {
    switch (selectedFilterChip) {
      case ChipIconType.MATCHED:
        return fileMatches.filter((f) => f.matched);
      case ChipIconType.UNMATCHED:
        return fileMatches.filter((f) => !f.matched);
      case ChipIconType.ERROR:
        return fileMatches.filter((f) => f.errorMsg);
      case ChipIconType.ALL:
      default:
        return fileMatches;
    }
  }, [selectedFilterChip, fileMatches]);

  useEffect(() => {
    setFileMatches(matchedFields(formattedFile));
  }, [formattedFile, setFileMatches, fileMatches.length]);

  const recalculateFile: (
    settings: ImportSettings,
  ) => Promise<RecalculateResponse> = useCallback(
    (settings: ImportSettings) => {
      const errorResult = {
        recalculated: false,
        error:
          'Looks like the settings you have selected do not match the file you are trying to upload. Please, try changing the settings.',
      };

      if (!rawFile) return Promise.reject(errorResult);

      return new Promise((resolve, reject) => {
        parseCsvPreview(
          rawFile,
          {
            header: settings.header,
            delimiter: settings.delimiter,
            escapeChar: settings.escapeChar,
          },
          {
            complete: (results) => {
              if (results.data.length === 0) {
                reject(errorResult);
                return;
              }

              const normalizedFile = normalizeCSVData(results);
              normalizedFile.filename = rawFile.name;

              if (
                normalizedFile.headers &&
                normalizedFile.headers.length < COLUMN_NUMBER_MIN
              ) {
                reject(errorResult);
                return;
              }

              setFormattedFile(normalizedFile);
              resolve({ recalculated: true, error: null });
            },
            error: () => {
              reject(errorResult);
            },
          },
        );
      });
    },
    [rawFile],
  );

  const handleFileUpload = useCallback(async () => {
    if (!rawFile || !formattedFile) {
      throw new Error('No file or formatted data available for upload.');
    }

    const matchedHeaders = fileMatches.map(
      (match) => match.field ?? 'unmatched',
    );

    const fileContainerHeader = detectHeaders(formattedFile.headers);

    await submitBulkUploadFile({
      file: rawFile,
      caseID: selectedCase.caseID,
      uploaderEmail: user?.email || '',
      userId: user?.uid || '',
      containsHeader: fileContainerHeader,
      headers: matchedHeaders.toString(),
      delimiter,
      escapeChar,
      onProgress: setUploadProgress,
    });
  }, [
    delimiter,
    escapeChar,
    fileMatches,
    formattedFile,
    rawFile,
    selectedCase.caseID,
    submitBulkUploadFile,
    user?.email,
    user?.uid,
  ]);

  const contextValue = useMemo(
    () => ({
      formattedFile,
      setFormattedFile,
      delimiter,
      setDelimiter,
      escapeChar,
      setEscapeChar,
      header,
      setHeader,
      fileUploadView,
      setFileUploadView,
      fileMatches,
      setFileMatches,
      helpDrawerOpen,
      setHelpDrawerOpen,
      importSettingsModalOpen,
      setImportSettingsModalOpen,
      rawFile,
      setRawFile,
      recalculateFile,
      selectedFilterChip,
      setSelectedFilterChip,
      filteredMatches,
      handleFileUpload,
      isFileLoading: isPending,
      uploadProgress,
      uploadHasError,
      setUploadHasError,
      isAllRequiredFieldsMatched,
      isAllRecommendedFieldsMatched,
      requiredFields,
      requiredMatchedFields,
    }),
    [
      formattedFile,
      delimiter,
      escapeChar,
      header,
      fileUploadView,
      fileMatches,
      helpDrawerOpen,
      importSettingsModalOpen,
      rawFile,
      recalculateFile,
      selectedFilterChip,
      filteredMatches,
      handleFileUpload,
      isPending,
      uploadProgress,
      uploadHasError,
      isAllRequiredFieldsMatched,
      isAllRecommendedFieldsMatched,
      requiredFields,
      requiredMatchedFields,
    ],
  );

  return (
    <UploadClaimsFileContext.Provider value={contextValue}>
      {children}
    </UploadClaimsFileContext.Provider>
  );
}

export const useUploadClaimsFileContext = () =>
  useContext(UploadClaimsFileContext);
